import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  FlaskConical, TestTubes, ScanLine, User, Clock, Loader2,
  ChevronDown, ChevronRight, AlertTriangle, TrendingUp, TrendingDown,
  Minus, FileText, Send, Printer, Mail, Check, X, RefreshCw, ArrowRight,
  ClipboardCheck,
  Activity, TriangleAlert, CircleCheck, NotebookPen, Maximize2,
  Minimize2, GripVertical, History, FileEdit, Wand2, Eye, EyeOff,
  CheckSquare, SquareIcon
} from 'lucide-react';

export default function LabsImagingView({ onComplete }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await api.get('/labs/by-patient');
      setPatients(res.data.patients || []);
      if (res.data.patients?.length > 0) {
        setExpandedPatient(prev => prev || res.data.patients[0].patient_id);
      }
    } catch (err) {
      console.error('Failed to load labs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSeedDemo = async () => {
    setSeeding(true);
    try {
      await api.post('/labs/seed-demo');
      await loadData();
      toast.success('Lab demo data seeded');
    } catch (err) {
      toast.error('Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const handleItemResponded = () => {
    loadData();
    if (onComplete) onComplete();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="labs-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (patients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="labs-empty">
        <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center mb-4">
          <FlaskConical className="w-7 h-7 text-teal-400" />
        </div>
        <p className="text-sm font-medium text-slate-600 mb-1">No pending lab results</p>
        <p className="text-xs text-slate-400 mb-4">All lab and imaging results have been reviewed</p>
        <Button variant="outline" size="sm" onClick={handleSeedDemo} disabled={seeding}
          className="text-xs gap-1.5" data-testid="seed-labs-btn">
          {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />}
          Load Demo Data
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-1" data-testid="labs-imaging-view">
      <div className="flex items-center justify-between px-1 mb-1">
        <p className="text-xs text-slate-500">
          <span className="font-semibold text-slate-700">{patients.length}</span> patient{patients.length !== 1 ? 's' : ''} with pending results
        </p>
        <Button variant="ghost" size="sm" onClick={loadData} className="h-6 text-[10px] gap-1 text-slate-400">
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>
      </div>

      {patients.map(patient => (
        <PatientLabCard
          key={patient.patient_id}
          patient={patient}
          expanded={expandedPatient === patient.patient_id}
          onToggle={() => setExpandedPatient(
            expandedPatient === patient.patient_id ? null : patient.patient_id
          )}
          onItemResponded={handleItemResponded}
        />
      ))}
    </div>
  );
}


/* ═══════════════════════════════════════
   PATIENT LAB CARD
   ═══════════════════════════════════════ */

function PatientLabCard({ patient, expanded, onToggle, onItemResponded }) {
  const { patient_id, patient_name, results, total_abnormal, has_critical } = patient;
  const [showFlags, setShowFlags] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set(results.map(r => r.id))); // All checked by default

  const allSelected = selectedIds.size === results.length;
  const noneSelected = selectedIds.size === 0;

  const toggleAll = (e) => {
    e.stopPropagation();
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(results.map(r => r.id)));
  };

  const toggleOne = (id, e) => {
    e?.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const borderColor = has_critical
    ? 'border-l-rose-400'
    : total_abnormal > 0
      ? 'border-l-amber-400'
      : 'border-l-emerald-400';

  const handleBadgeClick = (e) => {
    e.stopPropagation();
    setShowFlags(prev => !prev);
  };

  return (
    <div className={`rounded-xl border border-slate-200 bg-white shadow-sm border-l-4 ${borderColor} transition-all`} style={{ overflow: 'visible' }}
      data-testid={`patient-lab-card-${patient_id}`}>
      <button onClick={onToggle}
        className="w-full text-left px-4 py-3.5 flex items-center gap-3 hover:bg-slate-50/50 transition-colors"
        data-testid={`patient-lab-toggle-${patient_id}`}>
        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">{patient_name}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {results.length} result{results.length !== 1 ? 's' : ''} pending review
            {selectedIds.size > 0 && selectedIds.size < results.length && (
              <span className="text-blue-500 ml-1">· {selectedIds.size} selected</span>
            )}
          </p>
        </div>
        {has_critical && (
          <div role="button" tabIndex={0} onClick={handleBadgeClick} onKeyDown={(e) => e.key==='Enter' && handleBadgeClick(e)}
            title={showFlags ? 'Hide inline flags' : 'Show inline flags'}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all cursor-pointer
              ${showFlags ? 'bg-rose-50 text-rose-600 border-rose-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
            data-testid={`flag-toggle-${patient_id}`}>
            <TriangleAlert className="w-3 h-3" /> Critical
            {showFlags ? <Eye className="w-3 h-3 ml-0.5" /> : <EyeOff className="w-3 h-3 ml-0.5" />}
          </div>
        )}
        {total_abnormal > 0 && !has_critical && (
          <div role="button" tabIndex={0} onClick={handleBadgeClick} onKeyDown={(e) => e.key==='Enter' && handleBadgeClick(e)}
            title={showFlags ? 'Hide inline flags' : 'Show inline flags'}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all cursor-pointer
              ${showFlags ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}
            data-testid={`flag-toggle-${patient_id}`}>
            <AlertTriangle className="w-3 h-3" /> {total_abnormal} abnormal
            {showFlags ? <Eye className="w-3 h-3 ml-0.5" /> : <EyeOff className="w-3 h-3 ml-0.5" />}
          </div>
        )}
        {total_abnormal === 0 && (
          <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px] gap-1 font-medium" variant="outline">
            <CircleCheck className="w-3 h-3" /> Normal
          </Badge>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-slate-100">
          <PatientToolbar patientId={patient_id} patientName={patient_name} results={results}
            selectedIds={selectedIds} onLabsSignedOff={onItemResponded} />
          {/* Select all checkbox row */}
          <div className="flex items-center gap-2 px-4 py-1.5 border-b border-slate-50 bg-slate-50/30">
            <button onClick={toggleAll} className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-slate-700 transition-colors"
              data-testid={`select-all-${patient_id}`}>
              {allSelected ? <CheckSquare className="w-3.5 h-3.5 text-blue-500" /> : <SquareIcon className="w-3.5 h-3.5" />}
              <span>{allSelected ? 'Deselect all' : 'Select all'}</span>
            </button>
            <span className="text-[9px] text-slate-300 ml-auto">{selectedIds.size}/{results.length} selected</span>
          </div>
          <div className="divide-y divide-slate-50">
            {results.map(result => (
              <LabResultItem key={result.id} result={result} onResponded={onItemResponded} showFlags={showFlags}
                isSelected={selectedIds.has(result.id)} onToggleSelect={(e) => toggleOne(result.id, e)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   PATIENT TOOLBAR
   ═══════════════════════════════════════ */

function PatientToolbar({ patientId, patientName, results, selectedIds, onLabsSignedOff }) {
  const [activePanel, setActivePanel] = useState(null);
  const [trending, setTrending] = useState(null);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [historical, setHistorical] = useState(null);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [letter, setLetter] = useState(null);
  const [letterLoading, setLetterLoading] = useState(false);
  const [letterMenuOpen, setLetterMenuOpen] = useState(false);

  const handleTrending = async () => {
    if (activePanel === 'trending') { setActivePanel(null); return; }
    setActivePanel('trending');
    if (trending) return;
    setTrendingLoading(true);
    try {
      const res = await api.get(`/labs/patient/${patientId}/trending`);
      setTrending(res.data);
    } catch (err) { toast.error('Failed to load trending'); }
    finally { setTrendingLoading(false); }
  };

  const handleHistorical = async () => {
    if (activePanel === 'historical') { setActivePanel(null); return; }
    setActivePanel('historical');
    if (historical) return;
    setHistoricalLoading(true);
    try {
      const res = await api.get(`/labs/patient/${patientId}/historical`);
      setHistorical(res.data.groups || []);
    } catch (err) { toast.error('Failed to load historical labs'); }
    finally { setHistoricalLoading(false); }
  };

  const handleGenerateLetter = async (letterType) => {
    setLetterMenuOpen(false);
    setActivePanel('letter');
    setLetter(null);
    setLetterLoading(true);
    try {
      let res;
      if (letterType === 'ai_draft') {
        res = await api.post(`/labs/patient/${patientId}/ai-draft-letter`, { soap_note_text: '' });
      } else {
        res = await api.post(`/labs/patient/${patientId}/generate-letter`, { letter_type: letterType });
      }
      setLetter(res.data.letter);
    } catch (err) { toast.error('Failed to generate letter'); }
    finally { setLetterLoading(false); }
  };

  const handleLetterSentFromToolbar = async () => {
    const ids = selectedIds ? [...selectedIds] : results.map(r => r.id);
    if (ids.length > 0) {
      try {
        await api.post('/labs/batch-signoff', { item_ids: ids });
        toast.success(`${ids.length} lab(s) signed off and delegated`);
      } catch { toast.error('Sign-off failed'); }
    }
    if (onLabsSignedOff) onLabsSignedOff();
  };

  return (
    <div className="bg-slate-50/70" style={{ overflow: 'visible', position: 'relative', zIndex: 10 }}>
      <div className="flex items-center gap-1.5 px-4 py-2.5 flex-wrap" style={{ overflow: 'visible' }}>
        <ToolbarBtn icon={Activity} label="Trending" active={activePanel === 'trending'}
          onClick={handleTrending} loading={trendingLoading} testId={`trending-btn-${patientId}`} />
        <ToolbarBtn icon={History} label="Previous Labs" active={activePanel === 'historical'}
          onClick={handleHistorical} loading={historicalLoading} testId={`historical-btn-${patientId}`} />

        {/* Sign Off dropdown — replaces the old "Lab Response" button */}
        <SignOffDropdown
          patientId={patientId}
          selectedIds={selectedIds}
          onSigned={() => {
            // Refresh the patient list after sign-off
            results.forEach(r => {
              if (selectedIds.has(r.id)) r._signed = true;
            });
            // Trigger parent refresh
            loadData && loadData();
          }}
        />

        {/* Letter dropdown */}
        <div className="relative" style={{ zIndex: 20 }}>
          <ToolbarBtn icon={FileText} label="Generate Letter" active={activePanel === 'letter'}
            onClick={() => setLetterMenuOpen(!letterMenuOpen)} loading={letterLoading}
            testId={`letter-btn-${patientId}`} hasDropdown />
          {letterMenuOpen && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg w-56 py-1"
              style={{ zIndex: 50 }}
              data-testid={`letter-menu-${patientId}`}>
              <LetterMenuItem icon={CircleCheck} label="All Normal" desc="Standard reassurance letter"
                onClick={() => handleGenerateLetter('normal_results')} />
              <LetterMenuItem icon={AlertTriangle} label="Abnormal — Come In" desc="Request follow-up visit"
                onClick={() => handleGenerateLetter('abnormal_results')} />
              <LetterMenuItem icon={Wand2} label="AI Draft" desc="AI writes from labs + SOAP + trends"
                onClick={() => handleGenerateLetter('ai_draft')} />
              <div className="border-t border-slate-100 my-1" />
              <LetterMenuItem icon={FileEdit} label="Blank Letter" desc="Dear X... [your text] ...Sincerely"
                onClick={() => handleGenerateLetter('blank')} />
            </div>
          )}
        </div>
      </div>

      {/* Panels */}
      {activePanel === 'trending' && (
        <div className="px-4 pb-3" data-testid={`trending-panel-${patientId}`}>
          {trendingLoading ? <PanelLoader text="Loading trends..." color="teal" /> :
            trending && Object.keys(trending.analytes).length > 0 ? (
              <div className="rounded-lg bg-white border border-teal-100 px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1.5 mb-3">
                  <Activity className="w-3.5 h-3.5 text-teal-500" />
                  <span className="text-[11px] font-semibold text-teal-600 uppercase tracking-wide">Value Trends</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(trending.analytes).map(([analyte, data]) => (
                    <TrendingCard key={analyte} analyte={analyte} data={data} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-white border border-slate-100 px-4 py-3 text-center">
                <p className="text-xs text-slate-400">No trending data available (requires 2+ data points).</p>
              </div>
            )}
        </div>
      )}

      {activePanel === 'historical' && (
        <div className="px-4 pb-3" data-testid={`historical-panel-${patientId}`}>
          {historicalLoading ? <PanelLoader text="Loading previous labs..." color="blue" /> :
            historical && historical.length > 0 ? (
              <div className="rounded-lg bg-white border border-indigo-100 px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1.5 mb-3">
                  <History className="w-3.5 h-3.5 text-indigo-500" />
                  <span className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wide">Previous Labs</span>
                </div>
                <div className="space-y-2">
                  {historical.map(group => (
                    <HistoricalLabGroup key={group.test_name} group={group} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-white border border-slate-100 px-4 py-3 text-center">
                <p className="text-xs text-slate-400">No previous lab results found.</p>
              </div>
            )}
        </div>
      )}

      {activePanel === 'letter' && (
        <div className="px-4 pb-3" data-testid={`letter-panel-${patientId}`}>
          {letterLoading ? <PanelLoader text="Drafting letter..." color="orange" /> :
            letter ? <LetterEditor letter={letter} patientId={patientId} patientName={patientName}
              onLetterSent={handleLetterSentFromToolbar} /> : null}
        </div>
      )}
    </div>
  );
}

function ToolbarBtn({ icon: Icon, label, active, onClick, loading, testId, hasDropdown }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all
        ${active ? 'bg-white text-slate-700 shadow-sm border border-slate-200' : 'text-slate-500 hover:bg-white/60 hover:text-slate-700'}`}
      data-testid={testId}>
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {label}
      {hasDropdown && <ChevronDown className="w-2.5 h-2.5 ml-0.5" />}
    </button>
  );
}

function LetterMenuItem({ icon: Icon, label, desc, onClick }) {
  return (
    <button onClick={onClick}
      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2.5 transition-colors"
      data-testid={`letter-opt-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs font-medium text-slate-700">{label}</p>
        <p className="text-[10px] text-slate-400">{desc}</p>
      </div>
    </button>
  );
}


// ── Sign Off Dropdown ─────────────────────────────────────────────────────
// Replaces "Lab Response" — 3 clean actions for the physician to resolve items

function SignOffDropdown({ patientId, selectedIds, onSigned }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // 'file' | 'nurse' | null
  const [note, setNote] = useState('');
  const [nurses, setNurses] = useState([]);
  const [selectedNurse, setSelectedNurse] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadNurses = async () => {
    try {
      const res = await api.get('/staff/my-frequent-recipients');
      setNurses((res.data.frequent || []).concat(res.data.all_staff || []).filter(s => ['nurse','front_desk','work_clerk'].includes(s.role)));
    } catch {}
  };

  const handleSignOff = async (action) => {
    setSubmitting(true);
    const ids = selectedIds ? Array.from(selectedIds) : [];
    try {
      await Promise.all(ids.map(id =>
        api.post(`/labs/${id}/mark-reviewed`, {
          note: note || (action === 'reviewed_filed' ? 'Reviewed and filed' : 'Reviewed — please follow up'),
          action,
          nurse_id: selectedNurse?.id || '',
          nurse_name: selectedNurse?.name || '',
        })
      ));
      const count = ids.length;
      toast.success(
        action === 'reviewed_filed'
          ? `${count} item${count > 1 ? 's' : ''} marked reviewed & filed`
          : `${count} item${count > 1 ? 's' : ''} reviewed and tasked to ${selectedNurse?.name || 'nurse'}`
      );
      setOpen(false); setMode(null); setNote(''); setSelectedNurse(null);
      onSigned?.();
    } catch { toast.error('Sign-off failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="relative" style={{ zIndex: 30 }}>
      <ToolbarBtn icon={ClipboardCheck} label="Sign Off" active={open}
        onClick={() => { setOpen(!open); setMode(null); }} hasDropdown
        testId={`signoff-btn-${patientId}`} />

      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-xl w-72 overflow-hidden"
          style={{ zIndex: 50 }} data-testid={`signoff-menu-${patientId}`}>

          {!mode && (
            <>
              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  Sign Off — {selectedIds?.size || 0} item{(selectedIds?.size || 0) !== 1 ? 's' : ''} selected
                </p>
              </div>
              {/* Option 1 — Quick file */}
              <button onClick={() => setMode('file')}
                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left border-b border-slate-50"
                data-testid="signoff-file-option">
                <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-800">Mark Reviewed &amp; File</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Reviewed, no action needed. Closes item.</p>
                </div>
              </button>
              {/* Option 2 — Task nurse */}
              <button onClick={() => { setMode('nurse'); loadNurses(); }}
                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left border-b border-slate-50"
                data-testid="signoff-nurse-option">
                <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                  <Send className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-800">Mark Reviewed, Task Nurse</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Add a note and send a task to your team.</p>
                </div>
              </button>
              {/* Option 3 — close menu, use Generate Letter */}
              <button onClick={() => setOpen(false)}
                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                data-testid="signoff-letter-option">
                <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center shrink-0 mt-0.5">
                  <FileText className="w-4 h-4 text-orange-500" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-800">Generate Patient Letter</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Use the "Generate Letter" button →</p>
                </div>
              </button>
            </>
          )}

          {/* File mode — optional note */}
          {mode === 'file' && (
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <button onClick={() => setMode(null)} className="text-slate-400 hover:text-slate-600">
                  <ChevronDown className="w-4 h-4 rotate-90" />
                </button>
                <p className="text-xs font-semibold text-slate-700">Mark Reviewed &amp; File</p>
              </div>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                placeholder="Optional note (e.g. 'Reviewed — no action required')"
                className="w-full text-xs px-2.5 py-2 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200" />
              <button onClick={() => handleSignOff('reviewed_filed')} disabled={submitting}
                className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-60"
                data-testid="signoff-file-submit">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Confirm &amp; File
              </button>
            </div>
          )}

          {/* Nurse mode — note + recipient */}
          {mode === 'nurse' && (
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <button onClick={() => setMode(null)} className="text-slate-400 hover:text-slate-600">
                  <ChevronDown className="w-4 h-4 rotate-90" />
                </button>
                <p className="text-xs font-semibold text-slate-700">Task Nurse</p>
              </div>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                placeholder="Task note (e.g. 'Schedule follow-up in 3 months')"
                className="w-full text-xs px-2.5 py-2 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-200" />
              <div>
                <p className="text-[10px] text-slate-400 mb-1.5 font-semibold uppercase tracking-wider">Send to</p>
                <div className="space-y-1">
                  {nurses.slice(0, 5).map(n => (
                    <button key={n.id} onClick={() => setSelectedNurse(selectedNurse?.id === n.id ? null : n)}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-left transition-all
                        ${selectedNurse?.id === n.id ? 'bg-blue-50 border border-blue-200 text-blue-700' : 'hover:bg-slate-50 border border-transparent text-slate-700'}`}>
                      <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-600 shrink-0">
                        {n.name?.split(' ').map(x => x[0]).join('').slice(0,2)}
                      </div>
                      <div>
                        <p className="font-medium">{n.name}</p>
                        <p className="text-[9px] text-slate-400 capitalize">{n.role?.replace('_', ' ')}</p>
                      </div>
                      {n.is_default && <span className="ml-auto text-[9px] text-amber-500">Default</span>}
                      {selectedNurse?.id === n.id && <Check className="ml-auto w-3.5 h-3.5 text-blue-500" />}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => handleSignOff('reviewed_nurse')} disabled={submitting || !selectedNurse}
                className="w-full py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-60"
                data-testid="signoff-nurse-submit">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send Task &amp; File
              </button>
            </div>
          )}

        </div>
      )}
    </div>
  );
}


function PanelLoader({ text, color }) {
  const colorMap = { blue: 'text-blue-500', teal: 'text-teal-500', orange: 'text-orange-500' };
  return (
    <div className="flex items-center gap-2 py-3">
      <Loader2 className={`w-4 h-4 animate-spin ${colorMap[color] || 'text-slate-500'}`} />
      <span className="text-xs text-slate-500">{text}</span>
    </div>
  );
}


/* ═══════════════════════════════════════
   LETTER EDITOR — Editable letter with actions
   ═══════════════════════════════════════ */

function LetterEditor({ letter, patientId, patientName, compact, onLetterSent }) {
  const [content, setContent] = useState(letter.content || '');
  const [status, setStatus] = useState(letter.status);
  const [saving, setSaving] = useState(false);
  const [actioning, setActioning] = useState(false);
  const [sendMode, setSendMode] = useState('staff'); // 'staff' | 'portal'
  const [recipients, setRecipients] = useState({ frequent: [], all_staff: [] });
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const dirty = content !== letter.content;

  // Load frequent recipients — same logic as DocumentInbox
  useEffect(() => {
    api.get('/staff/my-frequent-recipients').then(r => {
      const data = r.data;
      setRecipients(data);
      const def = (data.frequent || []).find(f => f.is_default);
      if (def) setSelectedRecipient(def);
      else if (data.frequent?.length > 0) setSelectedRecipient(data.frequent[0]);
      else if (data.all_staff?.length > 0) setSelectedRecipient(data.all_staff[0]);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/labs/letters/${letter.id}/content`, { content });
      toast.success('Letter saved');
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleAction = async (action) => {
    if (dirty) { await handleSave(); }
    setActioning(true);
    try {
      await api.post(`/labs/letters/${letter.id}/status`, { status: action });
      setStatus(action);
      toast.success(`Letter marked as ${action}`);
    } catch (err) { toast.error('Failed to update'); }
    finally { setActioning(false); }
  };

  const handleSend = async () => {
    if (dirty) { await handleSave(); }
    setActioning(true);
    try {
      if (sendMode === 'portal') {
        await api.post(`/labs/letters/${letter.id}/status`, { status: 'sent' });
        setStatus('sent');
        toast.success('Letter queued for patient portal');
      } else {
        const staffName = selectedRecipient?.name || 'Nurse';
        const convRes = await api.post('/conversations', {
          patient_id: patientId,
          patient_name: patientName || 'Patient',
          title: `Lab letter for ${patientName || 'Patient'} — please print and send`,
          conversation_type: 'lab_review',
          pending_with: 'nurse',
          body: `Please process this lab result letter for ${patientName || 'the patient'}. Print, mail, or send to patient as appropriate.\n\n--- Letter Content ---\n${content}`,
          linked_artifacts: [{ artifact_type: 'lab_letter', artifact_id: letter.id, title: 'Lab Result Letter' }],
        });
        // Set action request so nurse sees completion buttons
        const convId = convRes.data?.conversation?.id;
        if (convId) {
          await api.post(`/conversations/${convId}/set-action`, {
            action_text: `Print and send lab letter to ${patientName || 'patient'}`,
            assigned_to: 'nurse',
          });
        }
        await api.post(`/labs/letters/${letter.id}/status`, { status: 'sent' });
        setStatus('sent');
        toast.success(`Letter sent to ${staffName}`);
      }
      // After successful send, sign off selected labs and close workspace
      if (onLetterSent) onLetterSent();
    } catch (err) {
      toast.error('Failed to send');
    } finally {
      setActioning(false);
    }
  };

  const recipientName = selectedRecipient?.name || 'Select...';

  return (
    <div className="rounded-lg bg-white border border-orange-100 shadow-sm overflow-hidden"
      data-testid={`letter-editor-${patientId}`}>
      <div className="px-3 py-2 bg-orange-50/50 border-b border-orange-100 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-orange-500" />
          <span className="text-[10px] font-semibold text-orange-600 uppercase tracking-wide">
            {letter.letter_type === 'ai_draft' ? 'AI Draft — Review Required' : 'Patient Letter'}
          </span>
          {letter.ai_generated && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[9px]" variant="outline">AI</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[10px] text-amber-600 font-medium">Unsaved</span>}
          <Badge variant="outline" className={`text-[9px] ${
            status === 'draft' ? 'border-slate-300 text-slate-500' :
            status === 'sent' ? 'border-emerald-300 text-emerald-600' :
            'border-blue-300 text-blue-600'}`}>{status}</Badge>
        </div>
      </div>

      <div className="p-3">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full text-sm text-slate-700 leading-relaxed font-serif border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300 resize-y"
          style={{ minHeight: compact ? '180px' : '200px' }}
          data-testid={`letter-textarea-${patientId}`}
        />
      </div>

      {status === 'draft' && (
        <div className="px-3 py-2 border-t border-orange-100 bg-orange-50/30 space-y-2">
          {/* Row 1: Print + Mail + Save */}
          <div className="flex items-center gap-1.5">
            {dirty && (
              <Button size="sm" variant="outline" className="text-[10px] gap-1 h-6"
                onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
              </Button>
            )}
            <Button size="sm" variant="outline" className="text-[10px] gap-1 h-6"
              onClick={() => handleAction('printed')} disabled={actioning}
              data-testid={`letter-print-${patientId}`}>
              <Printer className="w-3 h-3" /> Print
            </Button>
            <Button size="sm" variant="outline" className="text-[10px] gap-1 h-6"
              onClick={() => handleAction('mailed')} disabled={actioning}
              data-testid={`letter-mail-${patientId}`}>
              <Mail className="w-3 h-3" /> Mail
            </Button>
          </div>

          {/* Row 2: Send button + nurse picker — same pattern as DocumentInbox */}
          <div className="flex gap-1.5">
            {sendMode === 'staff' ? (
              <>
                <Button size="sm" className="flex-1 h-8 text-[10px] gap-1 bg-blue-600 hover:bg-blue-700"
                  onClick={handleSend} disabled={actioning || !selectedRecipient}
                  data-testid={`letter-send-${patientId}`}>
                  {actioning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Send to {recipientName}
                </Button>
                {/* Recipient picker caret */}
                <div className="relative">
                  <Button size="sm" variant="outline" className="h-8 px-2 border-blue-200"
                    onClick={() => setShowRecipientPicker(!showRecipientPicker)}
                    data-testid={`recipient-picker-btn-${patientId}`}>
                    <ChevronDown className={`w-3 h-3 text-blue-500 transition-transform ${showRecipientPicker ? 'rotate-180' : ''}`} />
                  </Button>
                  {showRecipientPicker && (
                    <div className="absolute right-0 bottom-full mb-1 w-56 bg-white rounded-xl border shadow-xl z-30 py-1 max-h-64 overflow-y-auto"
                      data-testid={`recipient-picker-${patientId}`}>
                      {recipients.frequent.length > 0 && (
                        <p className="px-3 py-1 text-[9px] text-slate-400 uppercase font-semibold">Quick Pick</p>
                      )}
                      {recipients.frequent.map(r => (
                        <button key={r.id} onClick={() => { setSelectedRecipient(r); setShowRecipientPicker(false); }}
                          className={`w-full text-left px-3 py-2 text-[10px] hover:bg-blue-50 flex items-center gap-2 ${selectedRecipient?.id === r.id ? 'bg-blue-50' : ''}`}>
                          {r.is_default && <span className="text-amber-500">★</span>}
                          {!r.is_default && <span className="w-3" />}
                          <span className="font-medium text-slate-700">{r.name}</span>
                          <span className="text-[8px] text-slate-400 ml-auto">{r.role} · {r.send_count}x</span>
                          {selectedRecipient?.id === r.id && <Check className="w-3 h-3 text-blue-500" />}
                        </button>
                      ))}
                      {recipients.all_staff.length > 0 && (
                        <>
                          {recipients.frequent.length > 0 && <div className="border-t my-1" />}
                          <p className="px-3 py-1 text-[9px] text-slate-400 uppercase font-semibold">All Staff</p>
                          {recipients.all_staff.filter(s => !recipients.frequent.find(f => f.id === s.id)).map(s => (
                            <button key={s.id} onClick={() => { setSelectedRecipient(s); setShowRecipientPicker(false); }}
                              className={`w-full text-left px-3 py-2 text-[10px] hover:bg-blue-50 flex items-center gap-2 ${selectedRecipient?.id === s.id ? 'bg-blue-50' : ''}`}>
                              <span className="w-3" />
                              <span className="text-slate-700">{s.name}</span>
                              <span className="text-[8px] text-slate-400 ml-auto">{s.role}</span>
                              {selectedRecipient?.id === s.id && <Check className="w-3 h-3 text-blue-500" />}
                            </button>
                          ))}
                        </>
                      )}
                      <div className="border-t my-1" />
                      <button onClick={() => { setSendMode('portal'); setShowRecipientPicker(false); }}
                        className="w-full text-left px-3 py-2 text-[10px] hover:bg-emerald-50 flex items-center gap-2 text-emerald-600">
                        <User className="w-3 h-3" />
                        <span className="font-medium">Patient Portal</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Button size="sm" className="flex-1 h-8 text-[10px] gap-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleSend} disabled={actioning}
                  data-testid={`letter-send-${patientId}`}>
                  {actioning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Send to Patient Portal
                </Button>
                <Button size="sm" variant="outline" className="h-8 px-2 border-emerald-200"
                  onClick={() => setSendMode('staff')}
                  data-testid={`switch-to-staff-${patientId}`}>
                  <ChevronDown className="w-3 h-3 text-emerald-500" />
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   SPLIT VIEW OVERLAY — Labs left, SOAP right
   ═══════════════════════════════════════ */

function HistoricalLabGroup({ group }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-indigo-100 bg-indigo-50/30 overflow-hidden">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-indigo-50/50 transition-colors"
        data-testid={`historical-group-${group.test_name.replace(/\s+/g, '-')}`}>
        <div className="flex items-center gap-2">
          <TestTubes className="w-3 h-3 text-indigo-400" />
          <span className="text-xs font-medium text-indigo-700">{group.test_name}</span>
          <Badge variant="outline" className="text-[9px] border-indigo-200 text-indigo-500">{group.count} prior</Badge>
        </div>
        <ChevronRight className={`w-3 h-3 text-indigo-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-2 space-y-2">
          {group.results.map((r, i) => (
            <div key={r.id || i} className="rounded-md bg-white border border-indigo-100 px-3 py-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-slate-400">{formatTime(r.created_at)}</span>
                {r.abnormal_count > 0 && (
                  <Badge className="text-[9px] bg-amber-50 text-amber-600 border-amber-200" variant="outline">
                    {r.abnormal_count} abnormal
                  </Badge>
                )}
              </div>
              {r.parsed_values?.length > 0 ? (
                <div className="space-y-0.5">
                  {r.parsed_values.map((v, j) => (
                    <div key={j} className="flex items-center gap-2 text-[11px]">
                      <span className="text-slate-600 w-32 truncate">{v.analyte}</span>
                      <span className={`font-mono font-medium ${
                        v.flag === 'H' ? 'text-rose-600' : v.flag === 'L' ? 'text-blue-600' : 'text-slate-700'
                      }`}>{v.value}{v.unit_suffix}</span>
                      {v.flag && <span className={`text-[9px] px-1 rounded ${
                        v.flag === 'H' ? 'bg-rose-100 text-rose-600' : 'bg-blue-100 text-blue-600'
                      }`}>{v.flag}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-600 whitespace-pre-wrap">{r.body?.substring(0, 200)}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   TRENDING CARD
   ═══════════════════════════════════════ */

function TrendingCard({ analyte, data }) {
  const { datapoints, direction, latest, previous } = data;
  const isUp = direction === 'up';
  const lastFlag = datapoints[datapoints.length - 1]?.flag;
  const isAbnormal = lastFlag === 'H' || lastFlag === 'L';
  const changePct = previous ? (((latest - previous) / previous) * 100).toFixed(1) : 0;

  const values = datapoints.map(dp => dp.value);
  const minVal = Math.min(...values) * 0.9;
  const maxVal = Math.max(...values) * 1.1;
  const range = maxVal - minVal || 1;

  const sparkPoints = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * 80 + 10;
    const y = 40 - ((v - minVal) / range) * 30 + 5;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${isAbnormal ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100 bg-slate-50/50'}`}
      data-testid={`trending-card-${analyte.replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-700 truncate">{analyte}</span>
        <div className="flex items-center gap-1">
          {isUp ? <TrendingUp className={`w-3 h-3 ${isAbnormal ? 'text-rose-500' : 'text-slate-400'}`} /> :
           direction === 'down' ? <TrendingDown className={`w-3 h-3 ${isAbnormal ? 'text-amber-500' : 'text-emerald-500'}`} /> :
           <Minus className="w-3 h-3 text-slate-400" />}
          <span className={`text-[10px] font-medium ${isUp && isAbnormal ? 'text-rose-600' : !isUp && !isAbnormal ? 'text-emerald-600' : 'text-slate-500'}`}>
            {changePct > 0 ? '+' : ''}{changePct}%
          </span>
        </div>
      </div>
      <div className="h-10 w-full">
        <svg viewBox="0 0 100 45" className="w-full h-full" preserveAspectRatio="none">
          <polyline points={sparkPoints} fill="none" stroke={isAbnormal ? '#f59e0b' : '#94a3b8'}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {values.map((v, i) => {
            const x = (i / Math.max(values.length - 1, 1)) * 80 + 10;
            const y = 40 - ((v - minVal) / range) * 30 + 5;
            const isLast = i === values.length - 1;
            return <circle key={i} cx={x} cy={y} r={isLast ? 3 : 2}
              fill={isLast ? (isAbnormal ? '#ef4444' : '#3b82f6') : (isAbnormal ? '#fbbf24' : '#cbd5e1')}
              stroke="white" strokeWidth="1" />;
          })}
        </svg>
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-slate-400">{datapoints.length} readings</span>
        <span className={`text-xs font-bold ${isAbnormal ? 'text-amber-700' : 'text-slate-700'}`}>
          {latest}{datapoints[datapoints.length - 1]?.flag ? ` (${datapoints[datapoints.length - 1].flag})` : ''}
        </span>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════
   LAB RESULT ITEM
   ═══════════════════════════════════════ */

function LabResultItem({ result, onResponded, compact, showFlags = true, isSelected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdf, setShowPdf] = useState(false);

  const isImaging = result.item_type === 'imaging_result';
  const TypeIcon = isImaging ? ScanLine : TestTubes;

  const handleViewDocument = async () => {
    if (pdfUrl) { setShowPdf(!showPdf); return; }
    if (!result.linked_document_id) return;
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('prattos_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${result.linked_document_id}/file`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/pdf') || ct.includes('image/')) {
          const blob = await res.blob();
          setPdfUrl(URL.createObjectURL(blob));
          setShowPdf(true);
        } else {
          // JSON response means no physical file — body text is the report
          setShowPdf(false);
        }
      }
    } catch {}
    finally { setPdfLoading(false); }
  };

  return (
    <div className={compact ? 'rounded-lg border border-slate-100 bg-white' : ''} data-testid={`lab-result-item-${result.id}`}>
      <div className={compact ? 'px-3 py-2.5' : 'px-4 py-3'}>
        <button onClick={() => setExpanded(!expanded)} className="w-full text-left flex items-center gap-3"
          data-testid={`lab-result-toggle-${result.id}`}>
          {/* Checkbox (if selectable) */}
          {onToggleSelect && (
            <button onClick={(e) => { e.stopPropagation(); onToggleSelect(e); }}
              className="flex-shrink-0 p-0.5" data-testid={`lab-check-${result.id}`}>
              {isSelected ? <CheckSquare className="w-4 h-4 text-blue-500" /> : <SquareIcon className="w-4 h-4 text-slate-300" />}
            </button>
          )}
          <div className={`p-1.5 rounded-lg flex-shrink-0 ${result.abnormal_count > 0 ? 'bg-amber-50' : 'bg-teal-50'}`}>
            <TypeIcon className={`w-3.5 h-3.5 ${result.abnormal_count > 0 ? 'text-amber-500' : 'text-teal-500'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-slate-700">{result.title}</p>
            {showFlags && result.abnormal_list.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {result.abnormal_list.map((flag, i) => (
                  <span key={i} className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-md font-medium ${
                    flag.includes('High') ? 'bg-rose-50 text-rose-600' : 'bg-blue-50 text-blue-600'
                  }`}>
                    {flag.includes('High') ? <TrendingUp className="w-2.5 h-2.5 mr-0.5" /> : <TrendingDown className="w-2.5 h-2.5 mr-0.5" />}
                    {flag}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[10px] text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />{formatTime(result.created_at)}
            </span>
            {result.is_critical && (
              <Badge className="bg-rose-50 text-rose-600 border-rose-200 text-[9px]" variant="outline">CRITICAL</Badge>
            )}
            <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </div>
        </button>

        {expanded && (
          <div className="mt-3 ml-9" data-testid={`lab-result-detail-${result.id}`}>
            {result.parsed_values.length > 0 && (
              <div className="rounded-lg border border-slate-100 overflow-hidden mb-3">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500">
                      <th className="text-left px-3 py-1.5 font-medium">Analyte</th>
                      <th className="text-right px-3 py-1.5 font-medium">Value</th>
                      <th className="text-center px-3 py-1.5 font-medium">Flag</th>
                      <th className="text-left px-3 py-1.5 font-medium">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.parsed_values.map((val, i) => (
                      <tr key={i} className={`border-t border-slate-50 ${val.is_abnormal ? 'bg-amber-50/40' : ''}`}>
                        <td className="px-3 py-1.5 text-slate-700 font-medium">{val.analyte}</td>
                        <td className={`px-3 py-1.5 text-right font-mono font-bold ${
                          val.flag === 'H' ? 'text-rose-600' : val.flag === 'L' ? 'text-blue-600' : 'text-slate-700'
                        }`}>{val.value}{val.unit_suffix}</td>
                        <td className="px-3 py-1.5 text-center">
                          {val.flag === 'H' && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-rose-100 text-rose-600 text-[10px] font-bold">H</span>}
                          {val.flag === 'L' && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold">L</span>}
                          {!val.flag && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-50 text-emerald-500 text-[10px]"><Check className="w-3 h-3" /></span>}
                        </td>
                        <td className="px-3 py-1.5 text-slate-400">{val.reference_range}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Report text — shown only when no structured values could be parsed */}
            {result.parsed_values.length === 0 && (
              <div className="mb-3">
                <OCRLabCard body={result.body} isImaging={isImaging} />
              </div>
            )}

            {/* View original PDF (for doc-manager sourced items) */}
            {result.linked_document_id && (
              <div className="mb-3">
                <button onClick={handleViewDocument} disabled={pdfLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs text-slate-600 transition-colors"
                  data-testid={`view-document-${result.id}`}>
                  {pdfLoading
                    ? <><span className="w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin" /> Loading...</>
                    : <><Eye className="w-3.5 h-3.5" /> {showPdf ? 'Hide' : 'View'} Original Report</>}
                </button>
                {showPdf && pdfUrl && (
                  <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden" style={{ height: '500px' }}>
                    <iframe src={pdfUrl} className="w-full h-full border-0" title="Lab/Imaging Report" />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * OCRLabCard — renders raw OCR lab text as a clean clinical report card.
 * Parses the vertical-table OCR format and renders structured rows with
 * color-coded abnormal flags. Falls back to clean formatted text if parsing fails.
 */
function OCRLabCard({ body, isImaging }) {
  if (!body) return null;

  // Strip fax header (everything up to and including the first empty-ish line after FROM:)
  const cleanBody = body.replace(/^FROM:.*?\n(?:.*?\n){0,5}/, '').trim();

  // Extract document metadata header (patient, DOB, DOS, ordering provider, etc.)
  const metaLines = [];
  const labLines = [];
  let inMeta = true;
  const META_KEYS = /^(Patient|DOB|Sex|MRN|DOS|Date of Service|Ordering Provider|Collected|Reported|Fasting|Acct|CLIA|Physician|Provider):/i;

  cleanBody.split('\n').forEach(line => {
    const t = line.trim();
    if (!t) return;
    if (inMeta && META_KEYS.test(t)) {
      metaLines.push(t);
    } else if (inMeta && t.toUpperCase() === t && t.length < 50 && !/\d/.test(t)) {
      // ALL CAPS section header like "HEMOGLOBIN A1c" or "LABORATORY REPORT"
      inMeta = false;
      labLines.push(t);
    } else {
      inMeta = false;
      labLines.push(t);
    }
  });

  // Try to detect vertical table rows from labLines
  // Format: test\nvalue\nunits\nreference\nflag (5-line groups)
  const COLUMN_HDRS = new Set(['test', 'result', 'units', 'reference range', 'flag', 'value', 'reference']);
  let tableRows = [];
  let headerIdx = labLines.findIndex((_, i) => {
    const run = [0,1,2,3,4].filter(j => labLines[i+j] && COLUMN_HDRS.has(labLines[i+j].toLowerCase())).length;
    return run >= 3;
  });

  if (headerIdx >= 0) {
    const dataStart = headerIdx + [0,1,2,3,4,5].filter(j => labLines[headerIdx+j] && COLUMN_HDRS.has(labLines[headerIdx+j].toLowerCase())).length;
    const data = labLines.slice(dataStart);
    let i = 0;
    while (i < data.length - 2) {
      const name = data[i];
      if (!name || (name === name.toUpperCase() && name.length < 40 && !/\d/.test(name))) { i++; continue; }
      const valStr = data[i+1] || '';
      const valMatch = valStr.match(/^([\d.]+)/);
      if (!valMatch) { i++; continue; }
      const val = parseFloat(valMatch[1]);
      const units = data[i+2] || '';
      const isUnitLine = units.length <= 8 && !/\d/.test(units.slice(0,2));
      let ref, flagRaw, stride;
      if (isUnitLine) {
        ref = data[i+3] || ''; flagRaw = (data[i+4] || '').toUpperCase(); stride = 5;
      } else {
        ref = units; flagRaw = (data[i+3] || '').toUpperCase(); stride = 4;
      }
      const flag = ['H','L'].includes(flagRaw) ? flagRaw : '';
      tableRows.push({ name, val, units: isUnitLine ? units : '', ref: ref.slice(0,60), flag });
      i += stride;
    }
  }

  const hasTable = tableRows.length > 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden text-xs">
      {/* Compact metadata header */}
      {metaLines.length > 0 && (
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex flex-wrap gap-x-4 gap-y-0.5">
          {metaLines.slice(0, 6).map((m, i) => (
            <span key={i} className="text-[10px] text-slate-500">{m}</span>
          ))}
        </div>
      )}

      {/* Structured table if detected */}
      {hasTable ? (
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500 border-b border-slate-100">
              <th className="text-left px-3 py-1.5 font-medium">Test</th>
              <th className="text-right px-3 py-1.5 font-medium">Result</th>
              <th className="text-left px-3 py-1.5 font-medium">Units</th>
              <th className="text-center px-3 py-1.5 font-medium">Flag</th>
              <th className="text-left px-3 py-1.5 font-medium">Reference</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, i) => (
              <tr key={i} className={`border-t border-slate-50 ${row.flag ? 'bg-amber-50/30' : ''}`}>
                <td className="px-3 py-1.5 font-medium text-slate-700">{row.name}</td>
                <td className={`px-3 py-1.5 text-right font-mono font-bold ${
                  row.flag === 'H' ? 'text-rose-600' : row.flag === 'L' ? 'text-blue-600' : 'text-slate-700'
                }`}>{row.val}{row.units ? ` ${row.units}` : ''}</td>
                <td className="px-3 py-1.5 text-slate-400 text-[10px]">{row.units}</td>
                <td className="px-3 py-1.5 text-center">
                  {row.flag === 'H' && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-rose-100 text-rose-600 text-[10px] font-bold">H</span>}
                  {row.flag === 'L' && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold">L</span>}
                  {!row.flag && <span className="inline-flex items-center justify-center w-4 h-4 text-emerald-400 text-[10px]">✓</span>}
                </td>
                <td className="px-3 py-1.5 text-slate-400 text-[10px]">{row.ref}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        /* Clean formatted text fallback — not a raw dump */
        <div className="px-3 py-2.5 space-y-0.5 max-h-64 overflow-y-auto">
          {labLines.map((line, i) => {
            if (!line.trim()) return null;
            const isHeader = line === line.toUpperCase() && line.length > 4 && !/\d/.test(line);
            const isValue = /^\d/.test(line.trim());
            const isFlag = ['H','L','HIGH','LOW','CRITICAL','NORMAL'].includes(line.trim().toUpperCase());
            return (
              <div key={i} className={
                isHeader ? 'text-[11px] font-bold text-slate-800 mt-2 border-b border-slate-100 pb-0.5' :
                isFlag && line.trim().toUpperCase() === 'H' ? 'text-[11px] font-bold text-rose-600' :
                isFlag && line.trim().toUpperCase() === 'L' ? 'text-[11px] font-bold text-blue-600' :
                isValue ? 'text-[12px] font-mono font-semibold text-slate-800' :
                'text-[11px] text-slate-600'
              }>{line}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}



function formatTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  } catch { return ''; }
}
